/**
 * What plays where. The soundtrack was written and performed in Logic Pro (see tools/audio);
 * this module decides which song fits the moment and keeps playlists from repeating.
 */

export type TrackId =
  | 'title'
  | 'lot-rent-shuffle'
  | 'porch-couch-breakdown'
  | 'cooler-full-of-natty'
  | 'bug-zapper-waltz'
  | 'tailgate-lullaby'
  | 'crick-water'
  | 'mullet-over'
  | 'satellite-dish'
  | 'widowmaker-hoedown';

export const TRACKS: Record<TrackId, { title: string }> = {
  title: { title: 'Double Wide Dreams' },
  'lot-rent-shuffle': { title: 'Lot Rent Shuffle' },
  'porch-couch-breakdown': { title: 'Porch Couch Breakdown' },
  'cooler-full-of-natty': { title: 'Cooler Full of Natty' },
  'bug-zapper-waltz': { title: 'Bug Zapper Waltz' },
  'tailgate-lullaby': { title: 'Tailgate Lullaby' },
  'crick-water': { title: 'Crick Water' },
  'mullet-over': { title: 'Mullet Over' },
  'satellite-dish': { title: 'She Took the Satellite Dish' },
  'widowmaker-hoedown': { title: 'Widowmaker Hoedown' },
};

/** The kind of music a place and time calls for. `quiet` lets the ambience have the stage. */
export type Mood = 'title' | 'day' | 'night' | 'backForty' | 'bar' | 'fair' | 'quiet';

export const PLAYLISTS: Record<Exclude<Mood, 'quiet'>, readonly TrackId[]> = {
  title: ['title'],
  day: ['lot-rent-shuffle', 'cooler-full-of-natty', 'porch-couch-breakdown', 'title'],
  night: ['bug-zapper-waltz', 'tailgate-lullaby', 'satellite-dish'],
  backForty: ['crick-water'],
  bar: ['mullet-over', 'satellite-dish', 'lot-rent-shuffle', 'widowmaker-hoedown', 'cooler-full-of-natty'],
  fair: ['widowmaker-hoedown', 'porch-couch-breakdown'],
};

export interface MoodInput {
  mode: 'title' | 'play' | 'end';
  /** Inside the Rusty Spur. */
  bar: boolean;
  /** Out at the rodeo and the mud bog. */
  fair: boolean;
  /** 0..1, how deep into the Back Forty. */
  murk: number;
  /** 0..1 darkness. */
  night: number;
  /** 0..1 storm strength. */
  storm: number;
}

export function pickMood(s: MoodInput): Mood {
  if (s.mode === 'title') return 'title';
  if (s.mode === 'end') return 'quiet';
  if (s.bar) return 'bar';
  // A tornado or a real storm: let the wind and the sirens do the talking.
  if (s.storm > 0.6) return 'quiet';
  if (s.murk > 0.45) return 'backForty';
  if (s.fair) return 'fair';
  return s.night > 0.55 ? 'night' : 'day';
}

/**
 * Plays every song in a list before repeating one, in a fresh order each time around, and
 * never the same song twice in a row.
 */
export class Shuffler {
  private readonly queues = new Map<Mood, TrackId[]>();
  private last: TrackId | null = null;

  constructor(private readonly random: () => number = Math.random) {}

  next(mood: Exclude<Mood, 'quiet'>): TrackId {
    const list = PLAYLISTS[mood];
    let queue = this.queues.get(mood);
    if (!queue || queue.length === 0) {
      queue = [...list];
      for (let i = queue.length - 1; i > 0; i--) {
        const j = Math.floor(this.random() * (i + 1));
        [queue[i], queue[j]] = [queue[j] as TrackId, queue[i] as TrackId];
      }
      if (queue.length > 1 && queue[0] === this.last) queue.push(queue.shift() as TrackId);
      this.queues.set(mood, queue);
    }
    const id = queue.shift() as TrackId;
    this.last = id;
    return id;
  }

  /** Forget where a mood's playlist was, so it starts fresh next time. */
  reset(mood: Mood): void {
    this.queues.delete(mood);
  }
}
